<template>
    <FormSection
        :full-height="false"
        title="Employee Scheduler"
        :application-id="visitDetails.Application"
        :show-product-pill="true"
        :show-background="false"
    >
        <template #content>
            <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                <span class="mt-1">
                    <b>Loading...</b>
                </span>
            </section>
            <section class="task-details" v-else>
                <div class="container row m-0 p-1">
                    <div class="form-group col-12 mt-2">
                        <div>
                            <input type="radio" v-model="isNva" :value="false" /> Visit Task
                            <input type="radio" v-model="isNva" :value="true" /> Non Visit Task
                        </div>
                        <strong for="task-name" class="me-2">Caregiver/Employee:</strong>
                        <span class="bold"> {{ visitDetails.FirstName }} {{ visitDetails.LastName }}</span>
                        <p class="mt-2">
                            <strong class="me-2">Agency:</strong>
                            <span>{{ visitDetails.AgencyName }}</span>
                        </p>
                    </div>
                    <div class="form-group col-12 col-md-3" v-if="isSingleDate">
                        <label for="date"> Date </label><span class="text-danger">*</span>
                        <DatePicker :getDate="taskDateChanged">
                            <div class="input-group date datepickerInput">
                                <input
                                    id="date"
                                    name="startDateTime"
                                    type="text"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    :value="formatDateInput(newVisitDetails.startDateTime.date)"
                                    @change="handleDateInputChange"
                                    :class="`form-control ${
                                        !v$.newVisitDetails.startDateTime.date.$error ? '' : 'field-invalid mt-0'
                                    }`"
                                    @blur="v$.newVisitDetails.startDateTime.date.$touch"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                            <span v-if="v$.newVisitDetails.startDateTime.date.$error" class="field-invalid"
                                >Date is required.</span
                            >
                        </DatePicker>
                    </div>

                    <!-- Statement Range -->
                    <div class="col-12 col-md-auto form-group" v-else>
                        <label for="date"> Date </label><span class="text-danger">*</span>
                        <div class="input-group flex-nowrap">
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        type="text"
                                        name="startDateTime"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(newVisitDetails.startDateTime.date)"
                                        @change="handleDateInputChange"
                                        :class="`form-control rounded-0 border-end-0 ${
                                            !v$.newVisitDetails.startDateTime.date.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.startDateTime.date.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.startDateTime.date.$error" class="field-invalid"
                                    >Start Date is required.</span
                                >
                            </DatePicker>
                            <div class="w-10">
                                <hr />
                            </div>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        disabled
                                        type="text"
                                        name="endDateTime"
                                        class="form-control rounded-0 border-end-0"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(newVisitDetails.endDateTime.date)"
                                        @change="handleDateInputChange"
                                        date-format-only
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                            </DatePicker>
                        </div>
                    </div>
                    <div class="form-group col-3" v-if="isNva">
                        <label for="branch">
                            Branch
                            <span class="text-danger ms-1">*</span>
                        </label>
                        <select class="form-select" v-model="selectedBranch" @blur="v$.selectedBranch.id.$touch">
                            <option value="null">Select Branch</option>
                            <option v-for="branch of selectedBranches" :value="branch" :key="branch.name">
                                {{ branch.name }}
                            </option>
                        </select>
                        <span v-if="v$.selectedBranch?.id?.$error" class="field-invalid">Branch is required.</span>
                    </div>
                    <div class="w-100"></div>
                    <div class="form-group col-10 col-md-3">
                        <label for="shift-length"> Shift Length </label>
                        <select
                            class="form-select"
                            v-model="shiftLength"
                            :disabled="scheduleWithoutTime || overrideShiftLength"
                        >
                            <option v-for="i of 12" :value="i">{{ i }} {{ i === 1 ? "Hour" : "Hours" }}</option>
                        </select>
                    </div>
                    <div class="col-auto form-group pb-2" v-if="!scheduleWithoutTime">
                        <label for="date"> Time </label><span class="text-danger">*</span>
                        <div>
                            <div class="input-group flex-nowrap">
                                <TimePicker @update-props="changeStartTime">
                                    <input
                                        placeholder="hh:mm AM"
                                        type="text"
                                        autocomplete="off"
                                        maxlength="8"
                                        :value="newVisitDetails.startDateTime.time"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.startDateTime.time.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.startDateTime.time.$touch"
                                    />
                                </TimePicker>

                                <div class="w-10">
                                    <hr />
                                </div>
                                <TimePicker @update-props="changeEndTime" id="schedule-hc-task-end-time">
                                    <input
                                        placeholder="hh:mm AM"
                                        type="text"
                                        class="form-control"
                                        autocomplete="off"
                                        maxlength="8"
                                        v-model="newVisitDetails.endDateTime.time"
                                        :disabled="!overrideShiftLength"
                                    />
                                </TimePicker>
                            </div>
                            <div v-if="v$.newVisitDetails.startDateTime.time.$error" class="field-invalid">
                                Time is required.
                            </div>
                        </div>
                    </div>
                    <div class="w-100"></div>
                    <div class="form-group form-check custom-checkbox col-11 mt-2 ms-3 d-block d-md-flex">
                        <div class="d-flex d-md-inline-block">
                            <input
                                type="checkbox"
                                id="schedule-without-time"
                                class="form-check-input"
                                v-model="scheduleWithoutTime"
                            />
                            <label class="form-check-label" for="schedule-without-time"> Schedule Without Time </label>
                        </div>
                        <div class="d-flex d-md-inline-block ms-md-5">
                            <input
                                type="checkbox"
                                id="override-shift-length"
                                class="form-check-input"
                                v-model="overrideShiftLength"
                            />
                            <label class="form-check-label" for="override-shift-length"> Override Shift Length </label>
                        </div>
                    </div>
                    <div class="w-100">
                        <hr />
                    </div>

                    <div class="form-group col-10 col-md-3">
                        <div class="w-100"></div>
                        <div class="form-group">
                            <label>Recurrence</label>
                            <select
                                class="form-select"
                                :value="newVisitDetails.recurrence.type"
                                @change="handleRecurrenceChange"
                                name="recurrence"
                            >
                                <option
                                    v-for="[key, value] of enumToMap(recurrenceType)"
                                    :key="key"
                                    :value="value.value"
                                >
                                    {{ value.name }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Daily -->

                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Daily">
                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        name="endDate"
                                        type="text"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        @change="handleDateInputChange"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>

                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat Every </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatNumber"
                                    name="repeat-every"
                                >
                                    <option v-for="i of 31" :value="i">{{ i }} {{ i == 1 ? "day" : "days" }}</option>
                                </select>
                            </div>
                        </div>
                    </template>

                    <!-- Weekly -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Weekly">
                        <div class="form-group col-6 col-md-3">
                            <label for="days" class="mb-2"> Repeat On </label><span class="text-danger">*</span>

                            <DayPicker
                                :selected-days-string="newVisitDetails.recurrence.daysOfWeek"
                                v-on:updateSelectedDays="updateDaysOfWeek"
                            >
                            </DayPicker>
                            <span v-if="v$.newVisitDetails.recurrence.daysOfWeek.$error" class="field-invalid"
                                >Repeat on Date is required.</span
                            >
                        </div>

                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        type="text"
                                        name="endDate"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        @change="handleDateInputChange"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>

                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat Every </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatNumber"
                                    name="repeat-every"
                                >
                                    <option v-for="i of 31" :value="i">{{ i }} {{ i == 1 ? "week" : "weeks" }}</option>
                                </select>
                            </div>
                        </div>
                    </template>

                    <!-- Monthly -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Monthly">
                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat Every </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatNumber"
                                    name="repeat-every"
                                >
                                    <option v-for="i of 31" :value="i">
                                        {{ i }} {{ i == 1 ? "month" : "months" }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="w-100"></div>

                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat By </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatBy"
                                    name="repeat-every"
                                >
                                    <option :value="1">Day of Month</option>
                                    <option :value="2">Day of Week</option>
                                </select>
                            </div>
                        </div>

                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        type="text"
                                        name="endDate"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        @change="handleDateInputChange"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>
                    </template>

                    <!-- Flexible -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Flexible">
                        <div class="w-100"></div>
                        <div class="form-group col-12">
                            <label for="date"> Select Days </label><span class="text-danger">*</span>
                            <MultiDatePicker :result="visitDates" v-on:updateResult="updateResult">
                                <input
                                    :class="`form-control ${
                                        !v$.newVisitDetails.recurrence.flexibleDates.$error ? '' : 'field-invalid mt-0'
                                    }`"
                                    @blur="v$.newVisitDetails.recurrence.flexibleDates.$touch"
                                />
                            </MultiDatePicker>
                            <span v-if="v$.newVisitDetails.recurrence.flexibleDates.$error" class="field-invalid"
                                >Dates are required.</span
                            >
                        </div>
                        <div></div>
                    </template>

                    <!-- 60 days -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.SixtyDays">
                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        type="text"
                                        name="endDate"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        @change="handleDateInputChange"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>
                    </template>

                    <!-- End of recurrence -->
                    <div v-if="!isNva" class="w-100">
                        <hr />
                    </div>
                    <div class="form-group col-10 col-md-6 col-lg-3" v-if="!isNva">
                        <label for="client"> Client </label><span class="text-danger">*</span>
                        <TypeaheadInput
                            id="client"
                            :getData="clientListFilter"
                            :value="client"
                            v-on:valueChanged="clientChanged"
                            placeholder="Type to Select User"
                            :show-all-by-default="true"
                            :open-on-focus="true"
                            ref="typeAheadRefClient"
                            v-on:blurredInput="v$.client?.$touch"
                            :class="`${!v$.client?.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.client?.$error" class="field-invalid">Client is required.</span>
                    </div>
                    <div class="w-100" v-if="!isNva">
                        <hr />
                    </div>
                    <div class="form-group col-10 col-md-5 col-lg-3" v-if="!isNva">
                        <label for="payment-source"> Payment Source </label><span class="text-danger">*</span>
                        <select
                            v-show="!isPaymentSourceLoading"
                            id="payment-source"
                            v-model="newVisitDetails.payorId"
                            :class="`form-select ${!v$.newVisitDetails.payorId.$error ? '' : 'field-invalid mt-0'}`"
                            @blur="v$.newVisitDetails.payorId.$touch"
                        >
                            <option :value="null">-- Select Payment Source --</option>
                            <option v-for="payor of payorList" :value="payor.insuranceId">
                                {{ payor.name }} ({{ payor.typeName }})
                            </option>
                        </select>
                        <span v-if="v$.newVisitDetails.payorId.$error" class="field-invalid"
                            >Payment Source is required.</span
                        >

                        <div v-show="isPaymentSourceLoading"><span class="shimmer-loading w-100 mt-2"></span></div>
                    </div>
                    <div class="form-group col-10 col-md-5 col-lg-3" v-if="!isNva">
                        <label for="task"> Task </label><span class="text-danger">*</span>
                        <div v-if="!!taskDropdownSelectOptions" class="multi-select-filter">
                            <GroupDropdownSelect
                                :drop-down-options="taskDropdownSelectOptions"
                                label-text="Task"
                                :selected-value="newVisitDetails.customNoteId"
                                :selected-text="newVisitDetails.taskName"
                                :selected-id="newVisitDetails.disciplineTask"
                                v-on:updateSelectFields="updateSelectFields"
                                :class="` ${!v$.newVisitDetails.disciplineTask.$error ? '' : 'field-invalid mt-0'}`"
                                @blur="v$.newVisitDetails.disciplineTask.$touch"
                            />
                            <span v-if="v$.newVisitDetails.disciplineTask.$error" class="field-invalid"
                                >Task is required.</span
                            >
                        </div>
                    </div>
                    <div class="form-group col-10 col-md-5 col-lg-3" v-if="isNva">
                        <label for="task"> Task </label><span class="text-danger">*</span>
                        <TypeaheadInput
                            :getData="nvaAvailableTasksFilter"
                            :value="nvaActivity"
                            v-on:valueChanged="nvaActivityChanged"
                            :show-all-by-default="true"
                            :open-on-focus="true"
                            :must-select-value="true"
                            placeholder="Type to Filter Task"
                            v-on:blurredInput="v$.nvaActivity.$touch"
                            :class="`${!v$.nvaActivity?.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.nvaActivity?.$error" class="field-invalid">Activity is required.</span>
                    </div>
                    <div class="w-100">
                        <hr />
                    </div>
                    <div class="col-10 col-md-6 form-group">
                        <label for="comments"> Comment </label>
                        <CommentInput
                            id="comments"
                            input-name="nonadmit-comments"
                            :show-template-typeahead="false"
                            :comment-required="false"
                            :result="newVisitDetails.comments"
                            :max-length="1000"
                            v-on:resultUpdated="resultUpdated"
                        />
                    </div>
                </div>
            </section>
            <div class="d-flex justify-content-center my-2" v-if="!isLoading">
                <div v-if="isSaving">
                    <button class="btn btn-primary">
                        <i class="fa fa-spinner fa-spin animated slow flash infinite"></i>
                    </button>
                </div>
                <div class="d-flex d-md-block justify-content-center flex-wrap-reverse flex-md-nowrap" v-else>
                    <button class="btn btn-primary my-1 my-md-0" @click="scheduleVisit(SaveEnum.SaveAndExit)">
                        Save and Exit
                    </button>
                    <SaveNotifyUser
                        :agency-id="newVisitDetails.agencyId"
                        button-name="Save and Notify Employee"
                        v-on:handleSaveVisit="scheduleVisit(SaveEnum.SaveAndNotifyEmployee)"
                    />
                    <button
                        class="btn btn-primary my-1 my-md-0 mx-2"
                        @click="scheduleVisit(SaveEnum.SaveAndAddAnother)"
                    >
                        Save and Add Another
                    </button>
                    <button type="button" class="btn btn-md btn-outline-secondary my-1 my-md-0" @click="cancel()">
                        Cancel
                    </button>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ScheduleVisitHomeCare.ts"></script>
